import { ExecutorEntity, PipelineRepository, PipelineStepParams } from '@telespot/domain';
import { LocalizedName, PipelineStepCategory, PipelineStepCategoryOption } from '@protocols/domain';

import { PipelineStepStructuredDraftDto } from '../../dtos/pipeline-step-draft.dto';
import type { ILabelNamesPort } from '../../ports/label-names.port';

export interface RetrievePipelineStepDetailResult {
  structured: PipelineStepStructuredDraftDto;
  rawParams: Record<string, unknown>;
}

export interface IRetrievePipelineStepDetailQuery {
  /**
   * Loads a PipelineStep with its full `params` JSON. Returns both the
   * editor-friendly structured draft (with labels resolved to multilingual
   * names) and the raw `params` JSON verbatim — the raw object preserves
   * unknown top-level keys so Manual Mode can round-trip them.
   *
   * @param pipelineStepId - id of the step to load.
   * @returns The hydrated structured draft alongside the raw params object.
   * @throws Propagates any error raised by the pipeline repository (including a
   * missing step) or by the label-name port. Callers are expected to handle
   * this in the effects layer.
   */
  execute(pipelineStepId: string): Promise<RetrievePipelineStepDetailResult>;
}

export class RetrievePipelineStepDetailQuery implements IRetrievePipelineStepDetailQuery {
  constructor(
    private readonly pipelineRepository: PipelineRepository,
    private readonly labelNamesPort: ILabelNamesPort
  ) {}

  public async execute(pipelineStepId: string): Promise<RetrievePipelineStepDetailResult> {
    const step = await this.pipelineRepository.getStepById(pipelineStepId);
    const rawParams: Record<string, unknown> = (step.params ?? {}) as Record<string, unknown>;
    const params = rawParams as Partial<PipelineStepParams>;
    const categorization = Array.isArray(params.categorization) ? params.categorization : [];
    const optionsMeta = params.options ?? {};

    const labelUuids = collectLabelUuids(categorization, optionsMeta);
    const namesByUuid = await this.labelNamesPort.resolve(labelUuids);

    const categories: PipelineStepCategory[] = categorization.map(
      (entry) =>
        new PipelineStepCategory({
          uuid: entry.category,
          name: namesByUuid[entry.category] ?? defaultName(entry.category),
          options: (entry.options ?? []).map<PipelineStepCategoryOption>((optUuid) => ({
            uuid: optUuid,
            name: namesByUuid[optUuid] ?? defaultName(optUuid),
            thrdis: typeof optionsMeta[optUuid]?.thrdisp === 'number' ? optionsMeta[optUuid].thrdisp : 0,
          })),
        })
    );

    const structured: PipelineStepStructuredDraftDto = {
      mode: 'structured',
      id: step.id,
      pipelineId: step.pipelineId,
      order: step.order,
      task: step.task,
      executorEntity: step.executorEntity,
      executorId: step.executorEntity === ExecutorEntity.ALGORITHM ? step.executorId : undefined,
      trigger: step.trigger,
      generateFinding: step.generateFinding,
      categories,
      multiple: !!params.multiple,
      allowNewOptions: !!params.allowNewOptions,
      enableMosaicView: !!params.enableMosaicView,
      counter: !!params.counter,
    };

    return { structured, rawParams };
  }
}

function collectLabelUuids(
  categorization: { category: string; options: string[] }[],
  optionsMeta: Record<string, unknown>
): string[] {
  const set = new Set<string>();
  for (const entry of categorization) {
    if (entry.category) set.add(entry.category);
    for (const optUuid of entry.options ?? []) {
      if (optUuid) set.add(optUuid);
    }
  }
  for (const uuid of Object.keys(optionsMeta)) {
    if (uuid) set.add(uuid);
  }
  return Array.from(set);
}

function defaultName(uuid: string): LocalizedName {
  return { en: uuid };
}
